/**
 * Progressive Search Hook
 *
 * Implements smart two-phase search:
 * 1. Instant search through already-loaded messages
 * 2. Deep search - automatically loads unloaded conversations and searches them
 *
 * Provides real-time progress updates and progressive results
 */

import { createLogger, randomUUID } from '@ctrl-enter/tools/utils';
import { useDeferredValue, useEffect, useRef, useState } from 'react';
import type { LocalChatMessage, LocalChatSession } from '../stores/s3-chat-store';
import { useS3ChatStore } from '../stores/s3-chat-store';
import { searchMessages, type MessageSearchResult } from '../utils/message-search';

const logger = createLogger('client:hook:use-progressive-search');
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3333/api';

type ProgressState = {
  isDeepSearching: boolean;
  loadedCount: number;
  totalCount: number;
  currentSession: string | null;
};

type UseProgressiveSearchOptions = {
  sessions: LocalChatSession[];
  query: string;
  enabled: boolean; // Only run when user has typed something
};

export const useProgressiveSearch = ({ sessions, query, enabled }: UseProgressiveSearchOptions) => {
  // Use the centralized store as single source of truth
  const updateSession = useS3ChatStore(state => state.updateSession);

  const [results, setResults] = useState<MessageSearchResult[]>([]);
  const [progress, setProgress] = useState<ProgressState>({
    isDeepSearching: false,
    loadedCount: 0,
    totalCount: 0,
    currentSession: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  // Simple flags for clarity:
  const isDeepSearchRunning = useRef<boolean>(false); // Is deep search currently running?
  const deepSearchCompleted = useRef<boolean>(false); // Did we finish loading everything?

  // Defer sessions update to prevent blocking UI when sessions change
  // Query is NOT deferred - we want immediate response to typing
  const deferredSessions = useDeferredValue(sessions);

  // Effect 1: Instant search - deferred sessions prevents blocking on tab switch
  useEffect(() => {
    if (!enabled || !query || query.trim().length < 2) {
      setResults([]);
      return;
    }

    // Instant search through loaded messages (updates as sessions load)
    // Uses deferred sessions so changes don't block the UI
    const instantResults = searchMessages(deferredSessions, query);
    setResults(instantResults);
  }, [deferredSessions, query, enabled]);

  // Effect 2: Deep search - runs once when search is enabled
  // Does NOT restart when query changes - sessions stay loaded
  useEffect(() => {
    // Only run deep search when enabled with valid query
    if (!enabled || !query || query.trim().length < 2) {
      // Clear progress when search is disabled
      setProgress({
        isDeepSearching: false,
        loadedCount: 0,
        totalCount: 0,
        currentSession: null,
      });

      // Abort any running deep search when user clears search
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        isDeepSearchRunning.current = false;
        deepSearchCompleted.current = false;
      }
      return;
    }

    // Phase 2: Deep search through unloaded sessions
    const unloadedSessions = sessions.filter(
      session => session.messages.length === 0 && session.messageCount > 0
    );

    // Check if there are actually no unloaded sessions
    if (unloadedSessions.length === 0) {
      // Nothing to deep search
      deepSearchCompleted.current = true;
      return;
    }

    // If there are unloaded sessions but flag says we're done, reset the flag
    if (deepSearchCompleted.current && unloadedSessions.length > 0) {
      deepSearchCompleted.current = false;
    }

    // Don't start a new deep search if one is already running
    if (isDeepSearchRunning.current) {
      return;
    }

    // Start deep search
    isDeepSearchRunning.current = true;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setProgress({
      isDeepSearching: true,
      loadedCount: 0,
      totalCount: unloadedSessions.length,
      currentSession: null,
    });

    // Load sessions one by one and update results progressively
    const deepSearch = async () => {
      for (let i = 0; i < unloadedSessions.length; i++) {
        if (controller.signal.aborted) break;

        const session = unloadedSessions[i];
        if (!session) continue;

        setProgress(prev => ({
          ...prev,
          loadedCount: i,
          currentSession: session.title,
        }));

        try {
          // Get access token (exact same way as use-chat-view-state.ts)
          let accessToken: string | null = null;
          try {
            // Web uses auth/token-manager directly
            const { getValidAccessToken } = await import('../auth/token-manager');
            accessToken = await getValidAccessToken();
          } catch (tokenError) {
            logger.error('[Progressive Search] Failed to get access token:', tokenError);
          }

          const authHeader = accessToken ? `Bearer ${accessToken}` : `Bearer dev-public-key`;

          // Fetch messages for this session
          // NOTE: Using credentials:'include' to send session cookies for authentication
          const { getDeviceId } = await import('../api/client');
          const response = await fetch(`${API_BASE_URL}/chat-history/${session.id}`, {
            method: 'GET',
            credentials: 'include', // Send cookies for session-based auth
            headers: {
              'Content-Type': 'application/json',
              Authorization: authHeader,
              'x-app-source': 'enter-ctrl-web', // Fixed: was 'enter-ctrl-desktop'
              'x-device-id': getDeviceId(), // CRITICAL: Include device ID for session validation
            },
            signal: controller.signal,
          });

          if (!response.ok) {
            logger.warn(
              '[Progressive Search] Failed to load session:',
              session.id,
              response.statusText
            );
            continue;
          }

          const result = await response.json();
          const chatSession = result.session;

          if (!chatSession?.messages) {
            continue;
          }

          // Convert S3 messages to local format (matching use-chat-view-state.ts format)
          const localMessages: LocalChatMessage[] = chatSession.messages.map((msg: any) => ({
            id: msg.id || randomUUID(),
            content: msg.content,
            sender: msg.sender,
            timestamp: msg.created_at,
            attachedFiles: (msg.attached_files || [])
              .map((att: any) => (typeof att === 'string' ? att : att?.url || ''))
              .filter((u: string) => typeof u === 'string' && u.length > 0),
            status: 'sent' as const,
          }));

          // Update the centralized store (single source of truth!)
          updateSession(session.id, {
            messages: localMessages,
            messageCount: localMessages.length,
            isLoading: false,
          });

          // Note: We don't search here anymore - Effect 1 (instant search) handles all searching
          // It will automatically re-run when deferredSessions updates with this newly loaded session
          // This prevents race conditions between deep search and instant search

          setProgress(prev => ({
            ...prev,
            loadedCount: i + 1,
          }));
        } catch (error: any) {
          if (error.name === 'AbortError') {
            break;
          }
          logger.error(`Error loading session ${session.id}:`, error);
        }
      }

      // Deep search complete successfully
      // Mark as completed
      isDeepSearchRunning.current = false;
      deepSearchCompleted.current = true;

      setProgress(prev => ({
        ...prev,
        isDeepSearching: false,
        currentSession: null,
      }));
    };

    deepSearch().catch(err => {
      logger.error('❌ [Progressive Search] Deep search failed:', err);
      isDeepSearchRunning.current = false;
    });

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [enabled]); // Only restart when search is enabled/disabled, not on query changes

  return {
    results,
    progress,
  };
};
